import { motion } from "framer-motion";
import { LEAF_COLORS, Leaf, LeafTreeFigure } from "@/components/landing/GeometricTree";

const GOLD = "#A9842F";
const GOLD_L = "#C8A24A";
const STONE_MID = "#DFD2B3";
const STONE_DARK = "#D4C5A1";
const STONE_LINE = "#C9BB98";
const CARVE = "#B9A87F";

/* Three nested pointed arches (sandstone, recessed like the reference) */
const ARCH_OUTER = "M100,570 L100,300 Q104,150 260,92 Q416,150 420,300 L420,570 Z";
const ARCH_MID = "M130,570 L130,310 Q134,175 260,122 Q386,175 390,310 L390,570 Z";
const ARCH_INNER = "M158,570 L158,318 Q162,196 260,150 Q358,196 362,318 L362,570 Z";

/* Carved eight-point star relief (embossed: light offset below-right) */
const CarvedStar = ({ cx, cy, s, o = 0.8 }) => (
  <g fill="none" opacity={o}>
    <g stroke="#F7F0DC" strokeWidth="1" transform="translate(1.1,1.1)">
      <rect x={cx - s} y={cy - s} width={2 * s} height={2 * s} />
      <rect x={cx - s} y={cy - s} width={2 * s} height={2 * s} transform={`rotate(45 ${cx} ${cy})`} />
    </g>
    <g stroke="#8F7C53" strokeWidth="1">
      <rect x={cx - s} y={cy - s} width={2 * s} height={2 * s} />
      <rect x={cx - s} y={cy - s} width={2 * s} height={2 * s} transform={`rotate(45 ${cx} ${cy})`} />
    </g>
  </g>
);

const fade = (delay) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.9, delay },
});

/* Sandstone Islamic archway (nested pointed arches, carved star friezes,
   masonry joints, checkered marble threshold) opening onto a charbagh
   garden with animated swaying trees, shimmering water and a fountain. */
export const ArchwayGarden = () => (
  <div
    data-testid="hero-archway-garden"
    className="relative w-[440px] h-[540px] lg:w-[500px] lg:h-[600px]"
    aria-hidden="true"
  >
    <svg viewBox="0 0 520 600" className="w-full h-full drop-shadow-[0_24px_40px_rgba(18,42,29,0.16)]">
      <defs>
        {LEAF_COLORS.map(([c1, c2], i) => (
          <linearGradient key={i} id={`leafG${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c1} />
            <stop offset="100%" stopColor={c2} />
          </linearGradient>
        ))}
        <clipPath id="archClip">
          <path d={ARCH_INNER} />
        </clipPath>
        <linearGradient id="wallG" x1="0" y1="0" x2="0.65" y2="1">
          <stop offset="0%" stopColor="#F5EDDA" />
          <stop offset="100%" stopColor="#D9CBA8" />
        </linearGradient>
        <linearGradient id="stoneFaceG" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#EBDFC2" />
          <stop offset="100%" stopColor="#D2C29B" />
        </linearGradient>
        <linearGradient id="innerFaceG" x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0%" stopColor="#F4EBD4" />
          <stop offset="100%" stopColor="#DECFAC" />
        </linearGradient>
        <linearGradient id="edgeL" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(86,66,36,0.22)" />
          <stop offset="100%" stopColor="rgba(86,66,36,0)" />
        </linearGradient>
        <linearGradient id="edgeR" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(86,66,36,0)" />
          <stop offset="100%" stopColor="rgba(86,66,36,0.22)" />
        </linearGradient>
        <filter id="softBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id="grain" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="2" seed="9" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.45  0 0 0 0 0.38  0 0 0 0 0.25  0 0 0 0.5 0" />
        </filter>
        <clipPath id="clipOuter">
          <path d={ARCH_OUTER} />
        </clipPath>
        <clipPath id="clipMid">
          <path d={ARCH_MID} />
        </clipPath>
        <linearGradient id="skyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F4ECD3" />
          <stop offset="100%" stopColor="#DDE8D2" />
        </linearGradient>
        <linearGradient id="lawnG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7FA85E" />
          <stop offset="100%" stopColor="#54824A" />
        </linearGradient>
        <linearGradient id="cypressG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2E5240" />
          <stop offset="50%" stopColor="#3E6B47" />
          <stop offset="100%" stopColor="#22422F" />
        </linearGradient>
        <linearGradient id="waterG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A9CCC6" />
          <stop offset="100%" stopColor="#7FB3AB" />
        </linearGradient>
        <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(240,224,172,0.5)" />
          <stop offset="60%" stopColor="rgba(227,201,122,0.18)" />
          <stop offset="100%" stopColor="rgba(227,201,122,0)" />
        </radialGradient>
        <linearGradient id="archVignette" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(11,31,22,0.12)" />
          <stop offset="55%" stopColor="rgba(11,31,22,0)" />
          <stop offset="100%" stopColor="rgba(11,31,22,0.45)" />
        </linearGradient>
      </defs>

      {/* outer recessed arch */}
      <motion.path {...fade(0.35)} d={ARCH_OUTER} fill="url(#stoneFaceG)" stroke={STONE_LINE} strokeWidth="1.5" />

      {/* recessed band between outer and mid arch */}
      <motion.g {...fade(0.45)}>
        <path d={`${ARCH_OUTER} ${ARCH_MID}`} fill={STONE_DARK} fillRule="evenodd" />
        <g clipPath="url(#clipOuter)">
          <path d={ARCH_OUTER} fill="none" stroke="rgba(86,66,36,0.4)" strokeWidth="10" filter="url(#softBlur)" />
        </g>
        <path d={ARCH_OUTER} fill="none" stroke="#FFF6DE" strokeWidth="1.2" strokeOpacity="0.55" />
      </motion.g>

      {/* light inner face between mid arch and the opening */}
      <motion.g {...fade(0.55)}>
        <path d={`${ARCH_MID} ${ARCH_INNER}`} fill="url(#innerFaceG)" fillRule="evenodd" />
        <g clipPath="url(#clipMid)">
          <path d={ARCH_MID} fill="none" stroke="rgba(86,66,36,0.4)" strokeWidth="9" filter="url(#softBlur)" />
        </g>
        <path d={ARCH_MID} fill="none" stroke="#FFF6DE" strokeWidth="1.2" strokeOpacity="0.6" />
      </motion.g>

      {/* ——— the leaf-rosette tree seen through the opening ——— */}
      <motion.g {...fade(0.7)} clipPath="url(#archClip)">
        <rect x="150" y="130" width="220" height="440" fill="url(#skyG)" />
        <circle cx="260" cy="205" r="115" fill="url(#sunGlow)" className="glow-pulse" />

        {/* soft lawn and ground shadow */}
        <rect x="150" y="430" width="220" height="140" fill="url(#lawnG)" opacity="0.5" />
        <ellipse cx="260" cy="552" rx="78" ry="9" fill="rgba(18,42,29,0.16)" />

        {/* tree E — the leaf-rosette tree */}
        <g transform="translate(78, 160) scale(0.7)">
          <LeafTreeFigure />
        </g>

        <rect x="150" y="130" width="220" height="440" fill="url(#archVignette)" />

        {/* shadow cast by the arch onto the garden */}
        <path d={ARCH_INNER} fill="none" stroke="rgba(15,30,20,0.5)" strokeWidth="16" filter="url(#softBlur)" />
      </motion.g>

      {/* opening edge: carved stone line + thin gold trim */}
      <motion.path {...fade(0.6)} d={ARCH_INNER} fill="none" stroke={CARVE} strokeWidth="2.5" />
      <motion.path {...fade(0.85)} d={ARCH_INNER} fill="none" stroke={GOLD_L} strokeWidth="1" strokeOpacity="0.85" />

      {/* stone grain texture on the arch */}
      <g clipPath="url(#clipOuter)">
        <rect
          x="100"
          y="90"
          width="320"
          height="482"
          filter="url(#grain)"
          opacity="0.4"
          style={{ mixBlendMode: "multiply" }}
        />
      </g>

      {/* floating satellite leaves */}
      <g className="animate-float">
        <Leaf ci={3} transform="translate(36,150) rotate(-30) scale(1.1)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "-2.5s" }}>
        <Leaf ci={1} transform="translate(486,120) rotate(40) scale(0.9)" />
      </g>
      <g className="animate-float" style={{ animationDelay: "-4.5s" }}>
        <Leaf ci={4} transform="translate(482,430) rotate(15) scale(1)" />
      </g>
    </svg>
  </div>
);
